// ============================================================================
// LES ONGLETS et LE SEGMENTÉ — deux contrôles qu'on confond, et qui ne disent pas la même chose.
//
// ⚠️ L'ONGLET BASCULE ENTRE DEUX VUES DU MÊME ÉCRAN. Si le choix change la ROUTE, ce sont des liens :
// `role="tablist"` y serait un mensonge d'arbre d'accessibilité — le lecteur d'écran annoncerait
// « onglet 2 sur 5 » pour une page qui change entièrement.
//
// ⚠️ ET UN `role="tab"` SE TIENT : tabulation glissante (un seul onglet dans l'ordre de tabulation),
// flèches pour circuler, `aria-controls` vers un panneau QUI EXISTE. Déclarés sans ces trois-là, les
// rôles annoncent un motif que le clavier ne rend pas — ce dépôt l'a déjà livré une fois.
// ============================================================================
import { useRef } from 'react';
import {
  RAIL_BREAKPOINT, SEG_ITEM, SEG_ITEM_STYLE, SEG_THUMB, SEG_TRACK, TABS_BADGE, TABS_BAR, TABS_ICON, TABS_INK, TABS_ITEM,
  TABS_ITEM_WEIGHT, TABS_MOTION, TABS_UNDERLINE, type IconName, type SegSize, type TabsSize,
} from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { OZ_SEG_THUMB } from './interactive';
import { Badge } from './badge';
import { useMediaMax } from './shell';
import { rovingRadio } from './roving';

export interface TabItem<K extends string = string> {
  id: K;
  label: string;
  /** Compteur. ⚠️ Absent à zéro par défaut — sauf si l'écran le veut visible (un « 0 » peut être une information). */
  count?: number;
  /** Ton du compteur, quand il alerte plutôt qu'il n'informe. */
  countTone?: 'neutral' | 'warn' | 'danger';
  /** Pictogramme posé avant le libellé (onglets d'un univers de statistiques). */
  icon?: IconName;
}

export interface TabsProps<K extends string = string> {
  items: readonly TabItem<K>[];
  value: K;
  onChange: (id: K) => void;
  /** Identifiant du panneau que ces onglets pilotent. ⚠️ Il doit EXISTER dans la page. */
  panelId: string;
  label: string;
  size?: TabsSize;
  /** Montrer un compteur à zéro (le journal des ventes en a besoin : « 0 annulation » informe). */
  showZero?: boolean;
}

/**
 * Quel onglet la touche désigne — ou `null` si elle ne nous concerne pas.
 *
 * ⚠️ ON CIRCULE, ON NE BUTE PAS : au dernier onglet, la flèche droite revient au premier. Buter en
 * silence donne l'impression d'un clavier mort, juste après que le lecteur d'écran a annoncé qu'on
 * pouvait circuler. `Origine`/`Fin` sautent aux bouts — une barre qui défile en compte onze.
 * ⚠️ Extraite du composant pour être MESURABLE : sans environnement DOM dans ce paquet, une
 * navigation au clavier écrite dans un gestionnaire d'événement n'est vérifiée par rien.
 */
export function tabAfterKey(key: string, i: number, total: number): number | null {
  if (total === 0) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return total - 1;
  const pas = key === 'ArrowRight' ? 1 : key === 'ArrowLeft' ? -1 : 0;
  if (!pas) return null;
  return (i + pas + total) % total;
}

export function Tabs<K extends string = string>({
  items, value, onChange, panelId, label, size = 'md', showZero = false,
}: TabsProps<K>) {
  const barreRef = useRef<HTMLDivElement | null>(null);
  const cran = TABS_ITEM[size];

  const auClavier = (e: React.KeyboardEvent, i: number) => {
    const target = tabAfterKey(e.key, i, items.length);
    if (target == null) return;
    e.preventDefault();
    const suivant = items[target]!;
    onChange(suivant.id);
    barreRef.current?.querySelector<HTMLElement>(`[data-tab="${suivant.id}"]`)?.focus();
  };

  return (
    <div
      ref={barreRef}
      role="tablist"
      aria-label={label}
      style={{
        display: 'flex', gap: TABS_BAR.gap,
        // ⚠️ FILET ET MARGE VIENNENT DU CRAN : dans un en-tête déjà refermé par son propre filet, la
        // barre en ajoutait un second à 1 px du premier, et réservait 20 px de vide sous elle.
        marginBottom: cran.spacingBelow,
        borderBottom: cran.rule ? `1px solid var(--${TABS_BAR.border})` : undefined,
        // ⚠️ LES DEUX AXES SE DÉCLARENT ENSEMBLE, TOUJOURS. Poser `overflow-x: auto` SEUL ne laisse pas
        // l'autre axe en `visible` : la spec CSS dit qu'un axe `visible` face à un axe qui ne l'est pas
        // se calcule en `auto`. `overflow-y` devenait donc `auto` — et comme chaque onglet porte
        // `margin-bottom: -1` (le trait d'actif doit chevaucher le filet de la barre), le contenu
        // dépassait la boîte d'un pixel : le navigateur affichait une BARRE DE DÉFILEMENT VERTICALE,
        // flèches comprises, à droite de la rangée d'onglets. Un ascenseur pour un pixel.
        overflowX: TABS_BAR.scrollX ? 'auto' : undefined,
        overflowY: TABS_BAR.scrollX ? 'hidden' : undefined,
      }}
    >
      {items.map((it, i) => {
        const actif = it.id === value;
        // ⚠️ `hideWhenZero` VIENT DU JETON : la règle « un compteur à zéro se cache » est une
        //    décision de charte, et `showZero` est l'exception que l'écran demande (le journal des
        //    ventes : « 0 annulation » informe). L'écrire ici en dur en aurait fait une seconde.
        const compteur = it.count != null && (it.count > 0 || showZero || !TABS_BADGE.hideWhenZero);
        return (
          <button
            key={it.id}
            type="button"
            role="tab"
            className="oz-tab"
            data-tab={it.id}
            id={`${panelId}-tab-${it.id}`}
            aria-selected={actif}
            aria-controls={panelId}
            // ⚠️ TABULATION GLISSANTE : un seul onglet dans l'ordre de tabulation. Sans elle, la
            // tabulation traverse les onglets un par un, et les flèches — que le lecteur d'écran vient
            // d'annoncer — ne font rien.
            tabIndex={actif ? 0 : -1}
            onKeyDown={(e) => auClavier(e, i)}
            onClick={() => onChange(it.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: TABS_BADGE.gap,
              height: cran.height, padding: `0 ${cran.padH}px`,
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: cran.fontSize,
              // ⚠️ La graisse NE CHANGE PAS : elle modifierait la largeur du libellé, donc tous les
              // onglets à droite glisseraient à chaque clic.
              fontWeight: TABS_ITEM_WEIGHT,
              color: actif ? `var(--${TABS_INK.active})` : `var(--${TABS_INK.idle})`,
              // Le trait est RÉSERVÉ dès le repos : l'activation ne décale jamais le texte.
              borderBottom: `${TABS_UNDERLINE.height}px solid ${actif ? `var(--${TABS_UNDERLINE.activeColor})` : 'transparent'}`,
              marginBottom: -1,
              transition: `color ${TABS_MOTION.transitionMs}ms, border-color ${TABS_MOTION.transitionMs}ms`,
              whiteSpace: 'nowrap',
            }}
          >
            {it.icon && <Icon name={it.icon} size={TABS_ICON.size} stroke={TABS_ICON.stroke} style={{ flexShrink: 0 }} />}
            {it.label}
            {compteur && (
              /* ⚠️ CRAN ET CHIFFRES TABULAIRES VIENNENT DU JETON `TABS_BADGE` (corrigé 2026-09-05).
                 Ils étaient réécrits ici : le jeton disait la même chose et personne ne le lisait —
                 exactement le défaut du pouce glissant, en plus discret puisque le rendu, lui,
                 était juste. Le jour où la charte change le cran, ce compteur n'aurait pas suivi. */
              <Badge tone={it.countTone ?? 'neutral'} size={TABS_BADGE.size}
                style={TABS_BADGE.numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}>
                {it.count}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── SEGMENTÉ ────────────────────────────────────────────────────────────────────────────────────

export interface SegmentedOption<K extends string = string> {
  id: K;
  label: string;
  /** Un picto devant le libellé — quand le choix est un objet qu'on reconnaît à sa forme (l'appareil d'un aperçu). */
  icon?: IconName;
}

export interface SegmentedProps<K extends string = string> {
  options: readonly SegmentedOption<K>[];
  /**
   * Valeur courante. ⚠️ `null` EST UNE VALEUR VALIDE : quand la valeur en vigueur n'est aucune des
   * options (des bornes de dates libres, par exemple), AUCUN segment ne s'allume. Un contrôle qui
   * désignerait quand même une option affirmerait un réglage qui n'est pas appliqué (§23.0).
   */
  value: K | null;
  onChange: (id: K) => void;
  label: string;
  size?: SegSize;
  /**
   * Forme de la piste. `track` (défaut) = coins de contrôle, la forme dominante.
   * ⚠️ `pill` est l'exception MESURÉE de la maquette : la piste des périodes du tableau de bord, qui
   * voisine un sélecteur de dates en pastille. On ne mélange pas les deux formes dans un même bandeau.
   */
  /**
   * SOUS `RAIL_BREAKPOINT`, la piste prend toute la largeur disponible et GLISSE horizontalement si
   * les segments débordent — la mécanique des onglets, appliquée au segmenté.
   * ⚠️ NÉ D'UN CAS RÉEL (État du stock, 2026-08-27) : quatre libellés ne tiennent pas sur un
   * téléphone, et un segmenté qui se replie en vrac sous la barre d'outils donne « 2-3 lignes » de
   * contrôles mélangés. Opt-in : les pistes courtes (périodes du tableau de bord) n'ont pas à
   * changer de géométrie. Au-dessus du seuil, `fluid` ne change RIEN.
   */
  fluid?: boolean;
}

/**
 * Deux à quatre choix EXCLUSIFS, tous visibles.
 * ⚠️ Au-delà de quatre, ce n'est plus un segmenté mais une liste qu'on ouvre (`Picker`) : un
 * cinquième segment casse la largeur de la piste avant de casser le sens.
 */
export function Segmented<K extends string = string>({
  options, value, onChange, label, size = 'md', fluid = false,
}: SegmentedProps<K>) {
  const cran = SEG_ITEM[size];
  const piste = SEG_TRACK;
  const galet = SEG_ITEM_STYLE.radius;
  // ⚠️ MÊME SEUIL QUE TOUT LE KIT (rail→tiroir, filtres→off-canvas) : un seuil propre au contrôle
  //    ferait basculer sa géométrie à un autre moment que le reste de la page.
  const serre = useMediaMax(RAIL_BREAKPOINT) && fluid;
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        display: serre ? 'flex' : 'inline-flex', gap: piste.gap, padding: piste.pad,
        ...(serre ? { width: '100%', boxSizing: 'border-box' as const, overflowX: 'auto' as const, overflowY: 'hidden' as const } : null),
        borderRadius: `var(--r-${piste.radius})`,
        background: `var(--${piste.bg})`,
        // ⚠️ La bordure sert : sans elle, une piste grise sur du papier n'a pas de bord — elle se lit
        // comme une zone désactivée, pas comme un creux dans lequel une pastille glisse.
        border: `1px solid var(--${piste.border})`,
      }}
    >
      {options.map((o, i) => {
        const actif = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            className="oz-seg"
            aria-checked={actif}
            // ⚠️ TABULATION GLISSANTE ET FLÈCHES : le rôle les promet, la porte partagée les rend.
            {...rovingRadio({ index: i, count: options.length, checked: actif, anyChecked: value !== null && options.some((x) => x.id === value), onPick: (k) => onChange(options[k]!.id) })}
            onClick={() => onChange(o.id)}
            style={{
              height: cran.height, padding: `0 ${cran.padH}px`, border: 'none',
              borderRadius: `var(--r-${galet})`,
              fontSize: cran.fontSize, fontWeight: SEG_ITEM_STYLE.weight, cursor: 'pointer',
              background: actif ? `var(--${SEG_ITEM_STYLE.activeBg})` : 'transparent',
              color: actif ? `var(--${SEG_ITEM_STYLE.activeInk})` : `var(--${SEG_ITEM_STYLE.idleInk})`,
              // Relief, pas assombrissement : sur un fond déjà creusé, foncer l'actif l'enfonce.
              boxShadow: actif ? `var(--sh-${SEG_ITEM_STYLE.activeShadow})` : undefined,
              transition: `background ${SEG_ITEM_STYLE.transitionMs}ms, color ${SEG_ITEM_STYLE.transitionMs}ms`,
              whiteSpace: 'nowrap',
              // En mode serré, les segments se PARTAGENT la largeur — sans rétrécir sous leur libellé.
              flex: serre ? '1 0 auto' : undefined,
              ...(o.icon ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : null),
            }}
          >
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * PISTE À POUCE GLISSANT — le sélecteur de période du tableau de bord.
 *
 * ⚠️ POURQUOI ELLE EXISTE, ET POURQUOI ELLE N'EXISTAIT PAS (2026-09-05, question utilisateur : « la
 * bulle active dans la maquette a un petit mouvement de déplacement quand elle change de case, mais
 * pas dans notre back-office — pourquoi ne pas avoir repris cet effet magnifique ? »). Les jetons
 * `SEG_THUMB` étaient posés depuis le portage, complets et justes : 74 × 30, encre unique, 220 ms,
 * la courbe exacte. **Aucun composant ne les lisait.** La doctrine avait été portée, le dessin non —
 * et un jeton que personne ne rend est une décision que personne ne peut voir.
 *
 * ⚠️ CE QUI LA DISTINGUE DU SEGMENTÉ À PASTILLE, et ce qui justifie deux composants plutôt qu'une
 * option : ici l'ENCRE NE CHANGE PAS. Tous les libellés gardent la même couleur, c'est le POUCE SEUL
 * qui dit l'état. C'est ce qui lui permet de n'en désigner AUCUN — quand la période vient de bornes
 * libres, le pouce s'efface et le contrôle cesse d'affirmer une valeur qui n'est pas en vigueur
 * (§23.0). Une piste à pastille ne sait pas exprimer ça : il y a toujours une pastille allumée.
 *
 * ⚠️ SEGMENTS DE LARGEUR FIXE, ET C'EST LE MOUVEMENT QUI L'EXIGE : un pouce qui se déforme en
 * glissant fait suivre au regard un objet qui change de taille. C'est aussi ce qui rend sa position
 * calculable sans mesurer le DOM.
 */
export interface SegmentedThumbProps<K extends string = string> {
  options: readonly { id: K; label: string }[];
  /** `null` — ou une valeur hors liste — efface le pouce plutôt que d'en allumer un au hasard. */
  value: K | null;
  onChange: (id: K) => void;
  label: string;
}

export function SegmentedThumb<K extends string = string>({
  options, value, onChange, label,
}: SegmentedThumbProps<K>) {
  const t = SEG_THUMB;
  const index = options.findIndex((o) => o.id === value);
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{
        position: 'relative', display: 'inline-flex', padding: t.pad,
        borderRadius: `var(--r-${t.trackRadius})`,
        background: `var(--${t.trackBg})`,
        border: `1px solid var(--${t.trackBorder})`,
      }}
    >
      {/* ⚠️ `aria-hidden` : le pouce est un DÉCOR. L'état est déjà porté par `aria-checked` de chaque
          bouton — l'annoncer deux fois ferait lire la sélection en double. */}
      <div
        aria-hidden
        className={OZ_SEG_THUMB}
        style={{
          position: 'absolute', top: t.pad, left: t.pad,
          width: t.segWidth, height: t.segHeight,
          borderRadius: `var(--r-${t.trackRadius})`,
          background: `var(--${t.thumbBg})`,
          boxShadow: 'var(--sh-sm)',
          // ⚠️ AUCUN POUCE QUAND LA VALEUR N'EST PAS DANS LA LISTE (`hiddenWhenUnset`) : c'est la
          //    raison d'être de cette forme, pas un cas limite.
          opacity: index < 0 && t.hiddenWhenUnset ? 0 : 1,
          transform: `translateX(${Math.max(0, index) * t.segWidth}px)`,
          transition: `transform ${t.transitionMs}ms ${t.easing}, opacity ${t.transitionMs}ms linear`,
        }}
      />
      {options.map((o, i) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={o.id === value}
          {...rovingRadio({ index: i, count: options.length, checked: o.id === value, anyChecked: index >= 0, onPick: (k) => onChange(options[k]!.id) })}
          onClick={() => onChange(o.id)}
          style={{
            position: 'relative', zIndex: 1,
            width: t.segWidth, height: t.segHeight,
            border: 'none', borderRadius: `var(--r-${t.trackRadius})`, background: 'transparent',
            fontSize: t.fontSize, fontWeight: t.weight, color: `var(--${t.ink})`,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
